import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtEur, fmtDateTime } from "@/lib/domain";
import { HardHat, FileText, TrendingUp, Wallet, ArrowRight, Activity } from "lucide-react";
import { Card } from "@/components/ui/card";

const KPI = ({ label, value, icon: Icon, accent = "amber", testId }) => {
  const accents = {
    amber:  "text-amber-400  bg-amber-500/10  border-amber-500/30",
    blue:   "text-blue-400   bg-blue-500/10   border-blue-500/30",
    emerald:"text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    purple: "text-purple-400 bg-purple-500/10 border-purple-500/30",
  };
  return (
    <div data-testid={testId} className="kpi-card rounded-xl border border-[#212b3d] bg-[#131924] p-5">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${accents[accent]}`}>
          <Icon size={18} />
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium mb-1">{label}</div>
      <div className="font-display font-bold text-3xl text-slate-100 leading-none">{value}</div>
    </div>
  );
};

const Tile = ({ to, label, sub, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`tile-${to?.slice(1) || label}`}
    className="group text-left rounded-xl border border-[#212b3d] bg-[#131924] hover:bg-[#1c2434] hover:border-amber-500/40 p-5 transition"
  >
    <div className="flex items-center justify-between mb-4">
      <div className="w-10 h-10 rounded-lg bg-[#0f1521] border border-[#212b3d] flex items-center justify-center text-slate-400 group-hover:text-amber-400 transition">
        <Icon size={18} />
      </div>
      <ArrowRight size={16} className="text-slate-600 group-hover:text-amber-400 transition" />
    </div>
    <div className="font-display font-semibold text-lg text-slate-100 mb-1">{label}</div>
    <div className="text-xs text-slate-500">{sub}</div>
  </button>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <div className="text-slate-400">Загрузка…</div>;

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400/80 mb-2 font-medium">
          Добро пожаловать, {user?.name?.split(" ")[0]}
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
          Панель управления
        </h1>
        <p className="text-slate-400 mt-1 text-sm">
          {isAdmin ? "Общая картина по всем объектам, клиентам и финансам." : "Ваши назначенные объекты и коммуникация."}
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testId="kpi-active-objects" label="Активные объекты" value={data.active_objects ?? 0} icon={HardHat} accent="amber" />
        {isAdmin && <KPI testId="kpi-open-offers"    label="Открытые КП"       value={data.open_offers ?? 0}      icon={FileText}   accent="purple" />}
        {isAdmin && <KPI testId="kpi-revenue"        label="Оборот (закрыт.)"  value={fmtEur(data.revenue_total)} icon={TrendingUp} accent="emerald" />}
        {isAdmin && <KPI testId="kpi-pending"        label="К оплате"          value={fmtEur(data.pending_payments)} icon={Wallet}  accent="blue" />}
        {!isAdmin && <KPI testId="kpi-total-objects" label="Всего объектов"    value={data.total_objects ?? 0}    icon={HardHat}    accent="blue" />}
      </div>

      {/* Quick tiles */}
      <div>
        <h2 className="font-display font-bold text-xl mb-4">Быстрый доступ</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Tile to="/objects"  label="Объекты"   sub="Все стройплощадки, статусы, фото" icon={HardHat}    onClick={() => nav("/objects")} />
          {isAdmin && <Tile to="/clients"  label="Клиенты"   sub="CRM: контакты, история, заметки" icon={require("lucide-react").Users}   onClick={() => nav("/clients")} />}
          {isAdmin && <Tile to="/quotes"   label="Предложения" sub="Создать КП, PDF, §13b UStG"    icon={FileText}   onClick={() => nav("/quotes")} />}
          {isAdmin && <Tile to="/workers"  label="Бригада"   sub="Мастера, часы, ставки"           icon={require("lucide-react").UsersRound} onClick={() => nav("/workers")} />}
          {isAdmin && <Tile to="/finances" label="Финансы"   sub="Маржа по объектам, отчёт"        icon={TrendingUp} onClick={() => nav("/finances")} />}
        </div>
      </div>

      {/* Recent activity */}
      <div>
        <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
          <Activity size={18} className="text-amber-400" /> Последняя активность
        </h2>
        <Card className="bg-[#131924] border-[#212b3d]">
          <div className="divide-y divide-[#212b3d]">
            {(data.recent_activity || []).length === 0 && (
              <div className="p-6 text-sm text-slate-500 text-center">Пока нет записей.</div>
            )}
            {(data.recent_activity || []).map((r, i) => (
              <button
                key={i}
                data-testid={`activity-row-${i}`}
                onClick={() => nav(`/objects/${r.object_id}`)}
                className="w-full text-left flex items-start gap-4 p-4 hover:bg-[#1c2434] transition"
              >
                <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-200 line-clamp-2">{r.text}</div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {r.author} · {fmtDateTime(r.created_at)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
